import { useEffect, useState, type ReactNode } from "react";
import type { MaintenanceStatus } from "@/api/settingsApi";
import Logo from "@/components/tpr/Logo/Logo";
import MaintenancePageView from "@/components/tpr/MaintenancePageView/MaintenancePageView";
import RandomWallpaper from "@/components/tpr/RandomWallpaper/RandomWallpaper";
import TprIcon from "@/components/tpr/TprIcon/TprIcon";
import { checkPublicMaintenanceStatus } from "@/utils/maintenanceCheck";
import { Button } from "@/components/ui/button";
import { Item, ItemContent } from "@/components/ui/item";
import { Spinner } from "@/components/ui/spinner";
import { login } from "@/core/auth";
import { gaEvent, gaPageView } from "@/core/google";
import { getKnownUser, clearKnownUser } from "@/core/knownUser";
import { useCheckEmail } from "@/hooks/useCheckEmail";
import { useSendOtp, useVerifyOtp, useSetPasswordWithOtp } from "@/hooks/useLoginOtp";
import { nextGatePath } from "@/utils/loginGates";
import { useNavigate, useSearchParams } from "react-router";
import EmailStep from "@/views/login/EmailStep";
import WelcomeBackStep from "@/views/login/WelcomeBackStep";
import VerifyAndSetStep from "@/views/login/VerifyAndSetStep";

type Step = "email" | "welcome-back" | "verify-and-set";

function otpMessage(reason?: string): string {
  switch (reason) {
    case "expired": return "That code has expired. Request a new one.";
    case "too_many_attempts": return "Too many attempts. Request a new code.";
    case "not_found": return "No active code. Request a new one.";
    default: return "That code is incorrect.";
  }
}

interface BannerProps {
  variant: "amber" | "green";
  children: ReactNode;
}

const Banner = ({ variant, children }: BannerProps) => {
  const colours =
    variant === "amber"
      ? "text-amber-700 bg-amber-100 dark:bg-amber-200 dark:text-amber-800"
      : "text-green-700 bg-green-100 dark:bg-green-200 dark:text-green-800";
  return (
    <div className={`p-4 mb-4 text-sm rounded-lg ${colours}`} role="alert">
      {children}
    </div>
  );
};

const Login = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [maintenanceStatus, setMaintenanceStatus] = useState<MaintenanceStatus | null>(null);

  // Step machine
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [stepError, setStepError] = useState<string | undefined>(undefined);

  // Known-user hint (pre-fills email and shows avatar/name on WelcomeBack)
  const knownUser = getKnownUser();

  const navigate = useNavigate();

  const { mutateAsync: checkEmail, isPending: checkPending } = useCheckEmail();
  const { mutateAsync: sendOtp, isPending: sendOtpPending } = useSendOtp();
  const { mutateAsync: verifyOtp, isPending: verifyOtpPending } = useVerifyOtp();
  const { mutateAsync: setPasswordWithOtp, isPending: setPasswordPending } = useSetPasswordWithOtp();

  useEffect(() => {
    gaPageView("/login");
    checkPublicMaintenanceStatus().then(setMaintenanceStatus);
    // Pre-fill from known-user hint — show welcome-back optimistically, then
    // re-validate in background and reconcile if the account state has changed.
    if (knownUser?.email) {
      setEmail(knownUser.email);
      setStep("welcome-back");
      checkEmail({ email: knownUser.email })
        .then((result) => {
          if (!result.exists) {
            // Account no longer found — clear hint and return to email step
            clearKnownUser();
            setEmail("");
            setStep("email");
          } else if (!result.hasPassword) {
            // Password was cleared since last visit — route to onboarding OTP flow
            sendOtp({ email: knownUser.email })
              .then(() => setStep("verify-and-set"))
              .catch(() => {
                // sendOtp failed — stay on welcome-back so user can try manually
              });
          }
          // exists && hasPassword → stay on welcome-back (no change needed)
        })
        .catch(() => {
          // Network error — stay on welcome-back and degrade gracefully
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchParams.get("sessionExpired") === "true") {
      setSessionExpired(true);
    }
    if (searchParams.get("resetSuccess") === "true") {
      setResetSuccess(true);
    }
  }, [searchParams]);

  // ── Step handlers ───────────────────────────────────────────────────────────

  const handleContinue = async () => {
    setNotFound(false);
    setStepError(undefined);
    try {
      const result = await checkEmail({ email });
      if (!result.exists) {
        setNotFound(true);
        return;
      }
      if (result.hasPassword) {
        setStep("welcome-back");
      } else {
        // Passwordless member — send OTP then move to verify-and-set
        try {
          await sendOtp({ email });
          setStep("verify-and-set");
        } catch {
          setStepError("Something went wrong sending the code. Please try again.");
        }
      }
    } catch {
      // check-email call failed (network error etc.) — degrade to welcome-back
      // so a flaky lookup never blocks the user from attempting to log in.
      setStep("welcome-back");
    }
  };

  const handlePasswordLogin = async () => {
    setLoading(true);
    setStepError(undefined);
    setSessionExpired(false);
    setResetSuccess(false);

    gaEvent("User", "Attempted Login");

    const response = await login(email, password);
    if (response instanceof Error) {
      const isAuthError =
        response.message.includes("Invalid email or password") ||
        response.message.includes("Error fetching member data after login") ||
        response.message.includes("Prospect members cannot login") ||
        response.message.includes("inactive");

      if (isAuthError) {
        setStepError(response.message);
        gaEvent("User", "Failed Login");
      } else {
        gaEvent("User", "System Error on Login");
        setError(true);
      }
      setLoading(false);
      return;
    }

    gaEvent("User", "Successful Login");
    // Keep the known-user hint (written by core/auth.ts login()) so the next visit
    // shows "Welcome back". Do NOT clear it here — that's only for "I'm not X".
    const gate = nextGatePath(response);
    setTimeout(() => {
      navigate(gate ?? "/dashboard", { replace: true });
    }, 1000);
  };

  const handleVerifyOtp = async (code: string): Promise<boolean> => {
    setStepError(undefined);
    try {
      const result = await verifyOtp({ email, code });
      if (!result.ok) {
        setStepError(otpMessage(result.reason));
        return false;
      }
      return true;
    } catch {
      setStepError("Something went wrong. Please try again.");
      return false;
    }
  };

  const handleSetPassword = async (code: string, newPassword: string) => {
    setStepError(undefined);
    setLoading(true);
    try {
      await setPasswordWithOtp({ email, code, password: newPassword });
      // Now log in with the new password
      const response = await login(email, newPassword);
      if (response instanceof Error) {
        setStepError(response.message);
        setLoading(false);
        return;
      }
      gaEvent("User", "Successful Login");
      // Keep the known-user hint for next visit's "Welcome back" (see note above).
      const gate = nextGatePath(response);
      setTimeout(() => {
        navigate(gate ?? "/dashboard", { replace: true });
      }, 1000);
    } catch (err) {
      setStepError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const handleNotMe = () => {
    clearKnownUser();
    setEmail("");
    setPassword("");
    setNotFound(false);
    setStepError(undefined);
    setStep("email");
  };

  const handleBack = () => {
    setStepError(undefined);
    setStep("email");
  };

  // ── Generic error screen ────────────────────────────────────────────────────

  const renderGenericError = () => (
    <div className="flex justify-center items-center flex-col gap-6 mt-8 md:max-w-2/3 md:mx-auto">
      <div className="text-center flex flex-col justify-center items-center">
        <div className="mb-4">
          <TprIcon size="large" />
        </div>
        <h2 className="text-4xl font-bold text-center text-gray-700 dark:text-white">
          <Logo color="black" size="large" />
        </h2>
      </div>
      <div
        className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800"
        role="alert"
      >
        We are experiencing problems connecting to our database, we are sorry. We are aware of this connectivity
        issue. Please try again a little later.
      </div>
      <Button variant="secondary" onClick={() => navigate("/", { replace: true })} aria-label="Go back">
        Go back
      </Button>
    </div>
  );

  const renderLoading = () => (
    <div className="flex justify-center items-center flex-col gap-6 mt-8">
      <Spinner className="h-12 w-12" />
      <span>One moment...</span>
    </div>
  );

  const renderStep = () => {
    if (step === "welcome-back") {
      // Only show the stored hint name/avatar when the hint email matches what was typed
      const hintMatches = knownUser?.email?.toLowerCase() === email.toLowerCase();
      return (
        <WelcomeBackStep
          firstName={hintMatches ? knownUser?.firstName : undefined}
          avatar={hintMatches ? knownUser?.avatar : undefined}
          email={email}
          password={password}
          onPasswordChange={setPassword}
          onSubmit={handlePasswordLogin}
          onNotMe={handleNotMe}
          pending={loading}
          error={stepError}
        />
      );
    }
    if (step === "verify-and-set") {
      return (
        <VerifyAndSetStep
          email={email}
          onVerify={handleVerifyOtp}
          onSetPassword={handleSetPassword}
          onBack={handleBack}
          pending={verifyOtpPending || setPasswordPending || loading}
          error={stepError}
        />
      );
    }
    // Default: email step
    return (
      <EmailStep
        email={email}
        onEmailChange={setEmail}
        onContinue={handleContinue}
        notFound={notFound}
        pending={checkPending || sendOtpPending}
        error={stepError}
      />
    );
  };

  const renderLoginCard = () => (
    <Item className="border border-gray-200 bg-gray-50 dark:border-gray-700 rounded-lg p-4">
      <ItemContent>
        <div className="flex flex-col gap-2">
          {resetSuccess && (
            <Banner variant="green">
              Your password has been reset successfully. You can now log in with your new password.
            </Banner>
          )}
          {sessionExpired && (
            <Banner variant="amber">
              Your session has expired. Please log in again to continue.
            </Banner>
          )}
          {renderStep()}
        </div>
      </ItemContent>
    </Item>
  );

  if (error) {
    return renderGenericError();
  }

  if (maintenanceStatus?.isMaintenanceMode) {
    return <MaintenancePageView message={maintenanceStatus.maintenanceMessage} />;
  }

  return (
    <>
      <div className="bg-white dark:bg-gray-900">
        <div className="flex justify-center h-screen">
          <RandomWallpaper />

          <div className="flex items-center w-full max-w-md px-6 mx-auto lg:w-3/6">
            <div className="flex-1">
              {/* Logo */}
              <div className="text-center flex flex-col justify-center items-center">
                <div className="mb-4">
                  <TprIcon size="large" />
                </div>
                <h2 className="text-4xl font-bold text-center text-gray-700 dark:text-white">
                  <Logo color="black" size="large" />
                </h2>

                <p className="mt-3 text-gray-500 dark:text-gray-300">
                  Sign in to book rooms &amp; manage your existing bookings.
                </p>
              </div>

              {loading ? renderLoading() : <div className="mt-8 w-full">{renderLoginCard()}</div>}
              <span className="text-gray-300 text-xs pt-2">
                {import.meta.env.VITE_COMMIT_HASH || "n/a"} &middot; {import.meta.env.VITE_APP_ENV || "n/a"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
